import {
  makeStyles,
  TableBody,
  TableFooter,
  TableRow
} from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { CornermarkList_cornerMarks_edges_node } from "@saleor/cornermarks/types/CornermarkList";
import { CornermarkListUrlSortField } from "@saleor/cornermarks/urls";
import { renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage } from "react-intl";

export interface CornermarkListProps
  extends ListProps,
    ListActions,
    SortPage<CornermarkListUrlSortField> {
  cornermarks: CornermarkList_cornerMarks_edges_node[];
}

const numberOfColumns = 14;

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colName: {
        width: "auto"
      },
      colID: {
        width: 80
      },
      colTitle: {
        width: "auto"
      },
      colColor: {
        width: 180
      },
      colCreated: {
        width: 200
      }
    }
  }),
  { name: "CornermarkList" }
);

const CornermarkList: React.FC<CornermarkListProps> = ({
  cornermarks,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort
}) => {
  const classes = useStyles({});
  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={cornermarks}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage
            defaultMessage="Thumbnail"
            description="Title Text"
          />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === CornermarkListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(CornermarkListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="ID" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          colSpan={2}
          direction={
            sort.sort === CornermarkListUrlSortField.text
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CornermarkListUrlSortField.text)}
        >
          <FormattedMessage defaultMessage="Text" description="Title Text" />
        </TableCellHeader>
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage
            defaultMessage="Text Color"
            description="Text Color"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage
            defaultMessage="Main Color"
            description="Main Color"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colName} colSpan={2}>
          <FormattedMessage
            defaultMessage="Secondary Color"
            description="Secondary Color"
          />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          colSpan={2}
          direction={
            sort.sort === CornermarkListUrlSortField.created
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CornermarkListUrlSortField.created)}
        >
          <FormattedMessage
            defaultMessage="Created Date"
            description="Created Date"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          cornermarks,
          cornermark => {
            const isSelected = cornermark ? isChecked(cornermark.id) : false;

            return (
              <TableRow
                selected={isSelected}
                hover={!!cornermark}
                key={cornermark ? cornermark.id : "skeleton"}
                onClick={cornermark && onRowClick(cornermark.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(cornermark.id)}
                  />
                </TableCell>
                <TableCell className={classes.colTitle} colSpan={2}>
                  {cornermark ? (
                    cornermark?.isGradientColour ? (
                      <div
                        style={{
                          background: ` linear-gradient(to right, ${cornermark.mainColor} 0%,${cornermark.secondaryColor} 100%)`,
                          width: "120px",
                          height: "30px",
                          textAlign: "center"
                        }}
                      >
                        <p
                          style={{
                            fontSize: "13px",
                            lineHeight: "30px",
                            color: cornermark.textColor,
                            textAlign: "center"
                          }}
                        >
                          {cornermark.titleText}
                        </p>
                      </div>
                    ) : (
                      <div
                        style={{
                          background: cornermark.mainColor,
                          width: "120px",
                          height: "30px",
                          textAlign: "center"
                        }}
                      >
                        <p
                          style={{
                            fontSize: "13px",
                            lineHeight: "30px",
                            color: cornermark.textColor,
                            textAlign: "center"
                          }}
                        >
                          {cornermark.titleText}
                        </p>
                      </div>
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colID}>
                  {cornermark ? cornermark.cornermarkId : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colTitle} colSpan={2}>
                  {cornermark ? cornermark.titleText : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colColor} colSpan={2}>
                  {cornermark ? cornermark.textColor : <Skeleton />}
                </TableCell>
                <TableCell
                  className={classes.colColor}
                  align="left"
                  colSpan={2}
                >
                  {cornermark ? cornermark.mainColor : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colColor} colSpan={2}>
                  {cornermark ? cornermark.secondaryColor : <Skeleton />}
                </TableCell>
                <TableCell
                  className={classes.colCreated}
                  align="left"
                  colSpan={2}
                >
                  {cornermark ? cornermark.created : <Skeleton />}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No Corner Mark found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

export default CornermarkList;
